import { Button, CheckmarkIcon } from '@kvdbil/components';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { savedSearch } from '~/App/shared/selectors/auctionSearchSelector';
import { useTranslation } from '~/Locale';
import { ProcessedAuctionStates } from '~/helpers/auction';
import { getLocalFilters } from '~/helpers/auctionSearch';
import SaveSearchDialog from '../SaveSearchDialog';
import BiddingButton from '../BiddingButton';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import { openBiddingView } from '~/App/shared/actions/objectViewDrawerActions';
import { isObjectViewDrawerOpen } from '~/App/shared/selectors/storeObject';

const Container = styled.div`
  margin-top: 32px;
`;

const SoldReservedWrapper = styled.div`
  margin-top: 16px;
`;

const SavedSearchButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

type Props = {
  auction: ProcessedAuction;
  buyMethod: AuctionType;
  auctionStates: ProcessedAuctionStates;
};

function BuyMethodButton({ auction, buyMethod, auctionStates }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const isBiddingOpen = useSelector(isObjectViewDrawerOpen);

  const { isClosed, isBuyNowAvailable } = auctionStates;

  const { t } = useTranslation();
  const filters = getLocalFilters(auction);
  const isSearchSaved = useSelector(savedSearch(filters)) !== undefined;
  const { isSoldByBuyNow, isReserved } = auction;

  const handleEvent = () => {
    dispatch(openBiddingView(auction.id, AuctionType.BUY_NOW, 'default'));
  };

  const openSavedSearchModal = () => {
    setModalOpen(true);
  };

  const handleClose = () => setModalOpen(false);

  function renderButton() {
    switch (buyMethod) {
      case AuctionType.BUY_NOW:
        if (!isSoldByBuyNow && !isReserved) {
          return (
            <Button
              fullWidth
              size="large"
              color="info"
              colorShade="dark"
              isDisabled={!isBuyNowAvailable}
              onClick={handleEvent}
              aria-haspopup="dialog"
              aria-expanded={isBiddingOpen}
              aria-controls="object-bid-reserve-drawer"
            >
              {t('Buy direct - CTA')}
            </Button>
          );
        } else {
          return (
            <SoldReservedWrapper>
              <SavedSearchButton
                fullWidth
                size="large"
                color="info"
                colorShade="dark"
                variant={isSearchSaved ? 'outline' : 'solid'}
                onClick={openSavedSearchModal}
                prefixIcon={isSearchSaved ? <CheckmarkIcon /> : null}
              >
                {isSearchSaved
                  ? t('Search watched')
                  : t('Create a saved search')}
              </SavedSearchButton>
            </SoldReservedWrapper>
          );
        }
      case AuctionType.BIDDING:
        return <BiddingButton auction={auction} isClosed={isClosed} />;
    }
  }

  return (
    <Container>
      {renderButton()}

      {modalOpen && <SaveSearchDialog handleClose={handleClose} />}
    </Container>
  );
}

export default BuyMethodButton;
